import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Home, Mail, Phone, User, DollarSign, AlertTriangle, CalendarClock, Trash2, Save, MapPin, ExternalLink, X, ArrowUpDown, Receipt, Download, UserPlus, Search } from "lucide-react";

export default function PropertyDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/properties/${id}/detail`);
      setData(data);
      setForm({
        address: data.property.address || "",
        unit_number: data.property.unit_number || "",
        owner_name: data.property.owner_name || "",
        owner_email: data.property.owner_email || "",
        annual_assessment: data.property.annual_assessment || 0,
        starting_balance: data.property.starting_balance || 0,
        notes: data.property.notes || "",
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load property"); }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        address: (form.address || "").trim(),
        unit_number: (form.unit_number || "").trim(),
        owner_name: (form.owner_name || "").trim(),
        owner_email: (form.owner_email || "").trim() || null,
        annual_assessment: parseFloat(form.annual_assessment) || 0,
        starting_balance: parseFloat(form.starting_balance) || 0,
        notes: form.notes || null,
      };
      await api.patch(`/admin/properties/${id}`, payload);
      toast.success("Property updated");
      await load();
    } catch (e) {
      console.error("Property save failed:", e);
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save changes");
    } finally { setSaving(false); }
  };

  const unlink = async () => {
    if (!window.confirm(`Unlink ${data.linked_user?.email}? They will lose homeowner access to this unit.`)) return;
    try {
      await api.post(`/admin/properties/${id}/unlink`);
      toast.success("Unlinked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cancelSettlement = async () => {
    if (!window.confirm("Void the active settlement offer? The original balance will be restored (deposit already paid on Option 2 stays credited).")) return;
    try {
      await api.post(`/admin/properties/${id}/settlement/cancel`);
      toast.success("Settlement voided");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deletePayment = async (tx_id) => {
    if (!window.confirm("Delete this transaction? Balance will recompute automatically.")) return;
    try {
      await api.delete(`/admin/payments/${tx_id}`);
      toast.success("Payment deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!data || !form) return <div className="max-w-5xl mx-auto p-8 text-[#5C6A64]">Loading…</div>;

  const p = data.property || {};
  const transactions = data.transactions || [];
  const arrangements = data.arrangements || [];
  const bookings = data.bookings || [];
  const balanceNum = Number(p.balance || 0);
  const streetView = `https://www.google.com/maps?q=${encodeURIComponent((p.address || "") + " " + (p.unit_number || ""))}&output=embed`;
  const balColor = balanceNum > 0 ? "text-[#C86650]" : "text-[#2C4C3B]";
  const statusBg = p.status === "current" ? "bg-[#2C4C3B]" : p.status === "partial" ? "bg-[#EADCC8] text-[#2C4C3B]" : "bg-[#C86650]";
  const fmt = (v) => Number(v || 0).toFixed(2);

  return (
    <div data-testid="property-detail-page" className="max-w-5xl mx-auto px-6 py-10 space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[#2C4C3B] hover:underline">
        <ArrowLeft className="w-4 h-4" /> Back to Admin Console
      </Link>

      {/* Header */}
      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardContent className="p-6 flex flex-wrap justify-between items-start gap-4">
          <div>
            <p className="text-xs tracking-[0.3em] uppercase text-[#C86650]">Property</p>
            <h1 className="font-serif text-3xl text-[#1A2421]">{p.address}</h1>
            <p className="text-[#5C6A64]">Unit {p.unit_number}</p>
            <div className="flex items-center gap-3 mt-3 text-sm text-[#1A2421]">
              <User className="w-4 h-4 text-[#C86650]" /> {p.owner_name}
              {p.owner_email && (<><span className="text-[#D5CFC4]">·</span><Mail className="w-4 h-4 text-[#C86650]" /><a href={`mailto:${p.owner_email}`} className="underline">{p.owner_email}</a></>)}
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs uppercase tracking-wider text-[#5C6A64]">Balance</p>
            <p className={`font-serif text-4xl ${balColor}`}>${fmt(p.balance)}</p>
            <Badge className={`${statusBg} mt-2`}>{p.status}</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Map + balance breakdown */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="rounded-2xl border-[#D5CFC4] overflow-hidden">
          <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2"><MapPin className="w-5 h-5 text-[#C86650]" /> Location</CardTitle></CardHeader>
          <CardContent className="p-0">
            <iframe
              title="map"
              data-testid="property-map"
              src={streetView}
              className="w-full h-64 border-0"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
            <div className="p-3 text-xs text-[#5C6A64]">
              <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(p.address + " " + (p.unit_number || ""))}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#2C4C3B] hover:underline">
                Open in Google Maps <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2"><DollarSign className="w-5 h-5 text-[#C86650]" /> Balance breakdown</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <BalRow label="Annual assessment" value={`$${fmt(p.annual_assessment)}`} />
            <BalRow label="Starting balance (prior years)" value={`$${fmt(p.starting_balance)}`} />
            <BalRow label="YTD payments received" value={`$${fmt(p.ytd_payments)}`} accent="green" testid="bal-ytd-payments" />
            {Number(p.ytd_credits || 0) > 0 && (
              <BalRow
                label="YTD settlement credits"
                value={`$${fmt(p.ytd_credits)}`}
                accent="green"
                testid="bal-ytd-credits"
              />
            )}
            <BalRow label="YTD paid (total)" value={`$${fmt(p.ytd_paid)}`} accent="green" bold testid="bal-ytd-paid" />
            <BalRow label="This year still due" value={`$${fmt(p.current_year_due)}`} />
            <BalRow label="Past due (prior years)" value={`$${fmt(p.past_due)}`} accent={Number(p.past_due || 0) > 0 ? "red" : "green"} />
            {Number(p.carried_credit || 0) > 0 && (
              <BalRow
                testid="bal-carried-credit"
                label="Credit carried from last year"
                value={`-$${fmt(p.carried_credit)} · applied to this year`}
                accent="green"
              />
            )}
            <BalRow label="Total balance" value={`$${fmt(p.balance)}`} accent={balanceNum > 0 ? "red" : "green"} bold />
            {Number(p.account_credit || 0) > 0 && (
              <BalRow
                testid="bal-account-credit"
                label="Account credit (overpayment)"
                value={`+$${fmt(p.account_credit)} · applies next year`}
                accent="green"
                bold
              />
            )}
            {p.last_payment_date && <BalRow label="Last payment" value={`${new Date(p.last_payment_date).toLocaleDateString()} · $${fmt(p.last_payment_amount)}`} />}
            <BalRow label="Linked account" value={data.linked_user ? (
              <span className="inline-flex items-center gap-2">{data.linked_user.email}
                <Button size="sm" variant="ghost" data-testid="unlink-account" onClick={unlink}><X className="w-3 h-3" /></Button>
              </span>
            ) : (
              <AssignMemberDialog propertyId={id} propertyAddress={p.address} onAssigned={load} />
            )} />
          </CardContent>
        </Card>
      </div>

      {/* Active settlement */}
      {p.active_settlement && (
        <Card className="rounded-2xl border-[#C86650] bg-[#FDF3F0]" data-testid="active-settlement-card">
          <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2 text-[#1A2421]"><AlertTriangle className="w-5 h-5 text-[#C86650]" /> Active Settlement</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <p><b>{p.active_settlement.option === "option1_65_paid_in_full" ? "Option 1 — 65% reduction (pay in full)" : "Option 2 — 50% reduction (payment plan)"}</b></p>
            <p>Original balance: ${p.active_settlement.original_balance?.toFixed(2)} · Discount: ${p.active_settlement.discount_amount?.toFixed(2)} · Amount due: ${p.active_settlement.amount_due?.toFixed(2)}</p>
            {p.active_settlement.deadline && <p>Deadline: <b>{p.active_settlement.deadline}</b></p>}
            <Button data-testid="cancel-settlement" variant="outline" size="sm" onClick={cancelSettlement} className="rounded-full border-[#C86650] text-[#C86650]">Void settlement</Button>
          </CardContent>
        </Card>
      )}

      {p.balance > 0 && !p.active_settlement && (
        <div className="flex flex-wrap gap-3">
          <SettlementDialog propertyId={id} balance={balanceNum} deadline={data.settlement_deadline} onDone={load} />
          <LetterDialog propertyId={id} balance={balanceNum} deadline={data.settlement_deadline} property={p} />
        </div>
      )}
      {p.balance > 0 && p.active_settlement && (
        <LetterDialog propertyId={id} balance={balanceNum} deadline={data.settlement_deadline} property={p} />
      )}

      {/* Editable fields */}
      <Card className="rounded-2xl border-[#D5CFC4]">
        <CardHeader><CardTitle className="font-serif text-lg">Edit property</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Address" testid="edit-address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
            <Field label="Unit #" testid="edit-unit" value={form.unit_number} onChange={(v) => setForm({ ...form, unit_number: v })} />
            <Field label="Owner name" testid="edit-owner-name" value={form.owner_name} onChange={(v) => setForm({ ...form, owner_name: v })} />
            <Field label="Owner email" testid="edit-owner-email" value={form.owner_email} onChange={(v) => setForm({ ...form, owner_email: v })} />
            <Field label="Annual assessment ($)" testid="edit-annual" type="number" value={form.annual_assessment} onChange={(v) => setForm({ ...form, annual_assessment: v })} />
            <Field label="Starting balance ($)" testid="edit-start-bal" type="number" value={form.starting_balance} onChange={(v) => setForm({ ...form, starting_balance: v })} help="Adjusts computed balance directly." />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes</label>
            <Textarea data-testid="edit-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <Button data-testid="save-property" onClick={save} disabled={saving} className="rounded-full bg-[#2C4C3B]"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save changes"}</Button>
        </CardContent>
      </Card>

      {/* Transactions ledger (filterable) */}
      <LedgerCard
        transactions={transactions}
        property={p}
        onDelete={deletePayment}
      />

      {/* Arrangements */}
      {arrangements.length > 0 && (
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[#C86650]" /> Payment arrangements</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {arrangements.map((a) => (
              <div key={a.id} className="p-3 rounded-xl bg-[#F9F8F6] border border-[#EADCC8]" data-testid={`arr-${a.id}`}>
                <div className="flex flex-wrap items-center gap-2"><Badge className={a.status === "active" ? "bg-[#C86650]" : a.status === "completed" ? "bg-[#2C4C3B]" : "bg-[#5C6A64]"}>{a.status}</Badge> <span>${fmt(a.total_amount)} · {a.installments} × {a.frequency} · started {a.first_due_date}</span></div>
                {a.settlement_option && <p className="text-xs text-[#C86650] mt-1">Settlement Option 2 — 50% reduction</p>}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Bookings history */}
      {bookings.length > 0 && (
        <Card className="rounded-2xl border-[#D5CFC4]">
          <CardHeader><CardTitle className="font-serif text-lg">Bookings ({bookings.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            {bookings.map((b) => (
              <div key={b.id} className="p-3 rounded-xl bg-[#F9F8F6] border border-[#EADCC8]" data-testid={`bk-${b.id}`}>
                <div className="flex flex-wrap items-center gap-2"><b>{b.amenity_name || b.amenity}</b> <span>· {b.date} · {b.time_slot}</span> <Badge className={b.status === "confirmed" ? "bg-[#2C4C3B]" : b.status === "pending" ? "bg-[#EADCC8] text-[#2C4C3B]" : "bg-[#C86650]"}>{b.status}</Badge></div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// --- Transactions Ledger --------------------------------------------------
// Filterable per-property transaction ledger with running balance.
// Types: payment (real money in), credit (board-applied settlement discount),
// deposit (arrangement upfront deposit posted against balance).
const TX_TYPES = [
  { key: "all", label: "All" },
  { key: "payment", label: "Payments" },
  { key: "credit", label: "Credits" },
  { key: "deposit", label: "Deposits" },
  { key: "cancelled", label: "Cancelled" },
];

// Friendly, human-readable label for the form-of-payment column in the transactions ledger.
// Mirrors the helper on the Admin Payments tab so the two views stay in sync.
function paymentMethodLabel(t) {
  const m = String(t.method || "").toLowerCase();
  const mode = String(t.mode || "").toLowerCase();
  if (m === "settlement_option1") return "Settlement · Option 1 (65% Off)";
  if (m === "settlement_option2") return "Settlement · Option 2 (50% Off Plan)";
  if (m === "settlement_credit" || t.lookup_key === "settlement_credit") return "Settlement credit (board applied)";
  if (m === "check") return "Check" + (t.check_number ? ` #${t.check_number}` : "");
  if (m === "cash") return "Cash";
  if (m === "money_order") return "Money order";
  if (m === "bank_transfer") return "Bank transfer";
  if (m === "arrangement_deposit") return "Arrangement deposit";
  if (m && !["credit_card", "card"].includes(m)) return m.replace(/_/g, " ");
  if (mode === "subscription_renewal") return "Credit card · Auto-pay renewal";
  if (mode === "subscription") return "Credit card · Auto-pay";
  if (mode === "payment") return "Credit card · Stripe";
  if (mode === "manual") return "Manual entry";
  return mode || "—";
}


function classifyTx(t) {
  const lk = String(t.lookup_key || "").toLowerCase();
  const method = String(t.method || t.mode || "").toLowerCase();
  if (lk === "settlement_credit" || method.startsWith("settlement_")) return "credit";
  if (lk.includes("deposit") || method === "arrangement_deposit") return "deposit";
  return "payment";
}

const TX_STYLES = {
  payment: { bg: "bg-[#2C4C3B]", text: "text-white", label: "Payment", amountCls: "text-[#2C4C3B]" },
  credit: { bg: "bg-[#C86650]", text: "text-white", label: "Credit", amountCls: "text-[#C86650] italic" },
  deposit: { bg: "bg-[#C86650]", text: "text-white", label: "Deposit", amountCls: "text-[#2C4C3B]" },
};

function LedgerCard({ transactions, property, onDelete }) {
  const [filter, setFilter] = useState("all");
  const [sortAsc, setSortAsc] = useState(false); // default newest-first

  // Enrich each tx with a "type" and compute running balance walking forward through time.
  // Only CONFIRMED (payment_status === 'paid') transactions reduce the running balance —
  // pending / failed / canceled Stripe checkouts must NEVER visually apply to the property
  // balance, matching how the backend _property_ledger computes the authoritative balance.
  const enriched = useMemo(() => {
    const initialOwed = Number(property.annual_assessment || 0) + Number(property.starting_balance || 0);
    const asc = [...transactions].sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    let cumulativePaid = 0;
    const withRunning = asc.map((t) => {
      const dollars = (t.amount || 0) / 100;
      const isConfirmed = (t.payment_status || "paid") === "paid";
      if (isConfirmed) cumulativePaid += dollars;
      return {
        ...t,
        _type: classifyTx(t),
        _dollars: dollars,
        _confirmed: isConfirmed,
        _runningBalance: Math.max(0, initialOwed - cumulativePaid),
      };
    });
    return withRunning;
  }, [transactions, property.annual_assessment, property.starting_balance]);

  const counts = useMemo(() => {
    const c = { all: enriched.length, payment: 0, credit: 0, deposit: 0, cancelled: 0 };
    enriched.forEach((t) => {
      c[t._type] = (c[t._type] || 0) + 1;
      if (!t._confirmed) c.cancelled += 1;
    });
    return c;
  }, [enriched]);

  // Sums only count CONFIRMED transactions — pending/failed dollars do NOT count toward totals.
  const sums = useMemo(() => {
    const s = { payment: 0, credit: 0, deposit: 0 };
    enriched.forEach((t) => { if (t._confirmed) s[t._type] = (s[t._type] || 0) + t._dollars; });
    return s;
  }, [enriched]);

  const pendingSum = useMemo(
    () => enriched.filter((t) => !t._confirmed).reduce((s, t) => s + t._dollars, 0),
    [enriched]
  );

  const filtered = useMemo(() => {
    const rows = filter === "all"
      ? enriched
      : filter === "cancelled"
        ? enriched.filter((t) => !t._confirmed)
        : enriched.filter((t) => t._type === filter);
    // Display order: newest first by default (reverse the asc-sorted enriched list)
    return sortAsc ? rows : [...rows].reverse();
  }, [enriched, filter, sortAsc]);

  const filterSum = filter === "all"
    ? enriched.filter((t) => t._confirmed).reduce((s, t) => s + t._dollars, 0)
    : sums[filter] || 0;

  return (
    <Card className="rounded-2xl border-[#D5CFC4]" data-testid="ledger-card">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="font-serif text-lg flex items-center gap-2">
            <Receipt className="w-5 h-5 text-[#C86650]" /> Transactions ledger
          </CardTitle>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="ledger-export-csv"
              onClick={() => {
                // Build CSV from the currently filtered + sorted rows so the export matches what admin sees.
                const header = ["Date", "Type", "Applied to", "Method", "Amount (USD)", "Status", "Balance after (USD)"];
                const escape = (v) => {
                  const s = String(v ?? "");
                  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
                };
                const rows = filtered.map((t) => [
                  t.created_at ? new Date(t.created_at).toISOString().slice(0, 10) : "",
                  TX_STYLES[t._type]?.label || t._type,
                  (t.lookup_key || "").replace(/_/g, " "),
                  t.method || t.mode || "",
                  (t._type === "credit" ? "-" : "") + t._dollars.toFixed(2),
                  (() => { const s = t.payment_status || "paid"; return s === "canceled" || s === "expired" ? "denied" : s; })(),
                  t._confirmed ? t._runningBalance.toFixed(2) : "not applied",
                ]);
                const csv = [header, ...rows].map((row) => row.map(escape).join(",")).join("\n");
                const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                const safeAddr = String(property.address || "property").replace(/[^A-Za-z0-9]+/g, "_");
                const safeUnit = String(property.unit_number || "").replace(/[^A-Za-z0-9]+/g, "_");
                const today = new Date().toISOString().slice(0, 10);
                link.href = url;
                link.download = `ledger_${safeAddr}_${safeUnit ? "unit_" + safeUnit + "_" : ""}${filter}_${today}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              }}
              className="inline-flex items-center gap-1 text-xs text-[#2C4C3B] border border-[#2C4C3B] rounded-full px-3 py-1 hover:bg-[#2C4C3B] hover:text-white transition-colors"
              title="Download the current filtered view as CSV"
            >
              <Download className="w-3 h-3" /> Export CSV
            </button>
            <button
              type="button"
              data-testid="ledger-sort-toggle"
              onClick={() => setSortAsc((v) => !v)}
              className="inline-flex items-center gap-1 text-xs text-[#5C6A64] hover:text-[#C86650]"
              title="Toggle chronological order"
            >
              <ArrowUpDown className="w-3 h-3" /> {sortAsc ? "Oldest first" : "Newest first"}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {TX_TYPES.map((t) => {
            const active = filter === t.key;
            const badgeColor = t.key === "payment" ? "bg-[#2C4C3B]" : t.key === "credit" ? "bg-[#EADCC8] text-[#2C4C3B]" : t.key === "deposit" ? "bg-[#C86650]" : t.key === "cancelled" ? "bg-[#C86650]" : "bg-[#5C6A64]";
            return (
              <button
                key={t.key}
                data-testid={`ledger-chip-${t.key}`}
                onClick={() => setFilter(t.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-all ${active ? "bg-[#1A2421] text-white border-[#1A2421]" : "bg-white text-[#5C6A64] border-[#D5CFC4] hover:border-[#C86650] hover:text-[#C86650]"}`}
              >
                {t.label}
                <span className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] ${active ? "bg-white text-[#1A2421]" : badgeColor + " text-white"}`}>
                  {counts[t.key] || 0}
                </span>
              </button>
            );
          })}
          <span className="ml-auto text-xs text-[#5C6A64] self-center">
            Showing <b className="text-[#1A2421]" data-testid="ledger-visible-count">{filtered.length}</b>
            {" · "}Total <b className="text-[#2C4C3B]" data-testid="ledger-visible-sum">${filterSum.toFixed(2)}</b> confirmed
            {pendingSum > 0 && (
              <span data-testid="ledger-pending-sum" className="ml-2 text-[#C86650]">· ${pendingSum.toFixed(2)} pending / not applied</span>
            )}
          </span>
        </div>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#EADCC8] text-[#2C4C3B]">
            <tr>
              <th className="text-left px-4 py-2">Date</th>
              <th className="text-left px-4 py-2">Type</th>
              <th className="text-left px-4 py-2">Applied to</th>
              <th className="text-left px-4 py-2">Method</th>
              <th className="text-right px-4 py-2">Amount</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2">Balance after</th>
              <th className="text-right px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-6 text-[#5C6A64]" data-testid="ledger-empty">
                  {enriched.length === 0
                    ? "No transactions on record."
                    : `No ${filter} transactions on record.`}
                </td>
              </tr>
            )}
            {filtered.map((t) => {
              const style = TX_STYLES[t._type];
              return (
                <tr key={t.id} data-testid={`ledger-row-${t.id}`} data-tx-type={t._type} className={`border-t border-[#D5CFC4] ${!t._confirmed ? "bg-[#FDF4F1]" : ""}`}>
                  <td className="px-4 py-2 text-xs whitespace-nowrap">
                    <div className="text-[#1A2421]">{t.created_at ? new Date(t.created_at).toLocaleDateString() : "—"}</div>
                    {t.created_at && <div className="text-[10px] text-[#5C6A64]" data-testid={`ledger-time-${t.id}`}>{new Date(t.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>}
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${style.bg} ${style.text}`}>{style.label}</span>
                  </td>
                  <td className="px-4 py-2 text-xs">{(t.lookup_key || "—").replace(/_/g, " ")}</td>
                  <td className="px-4 py-2 text-xs">{t.method || t.mode || "—"}</td>
                  <td className={`px-4 py-2 text-right whitespace-nowrap ${style.amountCls} ${!t._confirmed ? "line-through text-[#C86650]" : ""}`}>{t._type === "credit" ? "–" : ""}${t._dollars.toFixed(2)}</td>
                  <td className="px-4 py-2">
                    {(() => {
                      const s = t.payment_status || "paid";
                      const cls = s === "paid" ? "bg-[#2C4C3B] text-white"
                        : s === "pending" ? "bg-[#EADCC8] text-[#2C4C3B]"
                        : "bg-[#C86650] text-white";
                      const label = s === "canceled" ? "Cancelled" : s === "expired" ? "Cancelled" : s === "failed" ? "Cancelled" : s;
                      return <span data-testid={`tx-status-${t.id}`} className={`inline-block px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider capitalize ${cls}`}>{label}</span>;
                    })()}
                  </td>
                  <td className={`px-4 py-2 text-right whitespace-nowrap text-xs ${t._confirmed ? "text-[#5C6A64]" : "text-[#D5CFC4] italic"}`} data-testid={`balance-after-${t.id}`}>
                    {t._confirmed ? `$${t._runningBalance.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <Button size="icon" variant="ghost" data-testid={`del-tx-${t.id}`} onClick={() => onDelete(t.id)}><Trash2 className="w-3 h-3 text-[#C86650]" /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function BalRow({ label, value, accent, bold, testid }) {
  const color = accent === "red" ? "text-[#C86650]" : accent === "green" ? "text-[#2C4C3B]" : "text-[#1A2421]";
  return (
    <div className="flex justify-between items-center border-b border-[#EADCC8] py-1 last:border-0" data-testid={testid}>
      <span className="text-[#5C6A64]">{label}</span>
      <span className={`${color} ${bold ? "font-serif text-lg" : ""}`}>{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", help }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-[#5C6A64]">{label}</label>
      <Input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-xl" data-testid={testid} />
      {help && <p className="text-xs text-[#5C6A64] mt-1">{help}</p>}
    </div>
  );
}

function AssignMemberDialog({ propertyId, propertyAddress, onAssigned }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [assigningId, setAssigningId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/admin/users")
      .then(({ data }) => setMembers(Array.isArray(data) ? data : []))
      .catch(() => setMembers([]))
      .finally(() => setLoading(false));
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return members.slice(0, 50);
    return members.filter((u) =>
      (u.email || "").toLowerCase().includes(q) ||
      (u.name || "").toLowerCase().includes(q)
    ).slice(0, 50);
  }, [members, search]);

  const assign = async (userId, email) => {
    if (!window.confirm(`Assign ${email} as the homeowner of ${propertyAddress || "this property"}? They'll gain homeowner-portal access to this unit.`)) return;
    setAssigningId(userId);
    try {
      await api.post(`/admin/properties/${propertyId}/assign`, { user_id: userId });
      toast.success(`Assigned to ${email}`);
      setOpen(false);
      onAssigned?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to assign");
    } finally {
      setAssigningId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="assign-member-btn" className="rounded-full border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#2C4C3B] hover:text-white h-7 px-3 text-xs">
          <UserPlus className="w-3 h-3 mr-1" /> Assign member
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="assign-member-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#1A2421]">Assign a member</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[#5C6A64]">
            Search and pick a registered member to link to <b>{propertyAddress}</b>. They'll immediately gain homeowner-portal access to this unit.
          </p>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6A64]" />
            <Input
              data-testid="assign-member-search"
              placeholder="Search by name or email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 rounded-xl h-11"
              autoFocus
            />
          </div>
          <div className="max-h-72 overflow-y-auto border border-[#EADCC8] rounded-xl divide-y divide-[#EADCC8]" data-testid="assign-member-list">
            {loading && <p className="p-4 text-sm text-[#5C6A64]">Loading members…</p>}
            {!loading && filtered.length === 0 && (
              <p className="p-4 text-sm text-[#5C6A64]">No members match "{search}"</p>
            )}
            {!loading && filtered.map((u) => (
              <div key={u.id} className="p-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A2421] truncate">{u.name || "(no name)"}</p>
                  <p className="text-xs text-[#5C6A64] truncate">{u.email}</p>
                </div>
                <Button
                  size="sm"
                  data-testid={`assign-member-select-${u.id}`}
                  disabled={assigningId === u.id}
                  onClick={() => assign(u.id, u.email)}
                  className="rounded-full bg-[#2C4C3B] hover:bg-[#1F3D2E] text-white text-xs h-8 px-3 whitespace-nowrap"
                >
                  {assigningId === u.id ? "Assigning…" : "Assign"}
                </Button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#5C6A64]">
            Members already linked to another property will be rejected. Unlink them there first.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SettlementDialog({ propertyId, balance, deadline, onDone }) {
  const [open, setOpen] = useState(false);
  const [option, setOption] = useState("option1_65_paid_in_full");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    installments: 6,
    first_due_date: new Date(Date.now() + 14 * 86400 * 1000).toISOString().slice(0, 10),
    frequency: "monthly",
    deposit_amount: "",
    deposit_method: "check",
    deposit_reference: "",
    notes: "",
  });

  const runPreview = async (opt) => {
    try {
      const { data } = await api.post(`/admin/properties/${propertyId}/settlement/preview?option=${opt}`);
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
  };

  useEffect(() => { if (open) runPreview(option); }, [open, option]); // eslint-disable-line

  const apply = async () => {
    setBusy(true);
    try {
      const payload = { option, notes: form.notes };
      if (option === "option2_50_payment_plan") {
        payload.first_due_date = form.first_due_date;
        payload.frequency = form.frequency;
        const monthly = parseFloat(form.monthly_amount);
        if (!isNaN(monthly) && monthly > 0) {
          payload.monthly_amount = monthly;
        } else {
          payload.installments = parseInt(form.installments, 10);
        }
        payload.deposit_amount = parseFloat(form.deposit_amount) || 0;
        payload.deposit_method = form.deposit_method;
        payload.deposit_reference = form.deposit_reference;
      }
      await api.post(`/admin/properties/${propertyId}/settlement/apply`, payload);
      toast.success("Settlement applied & homeowner emailed");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-settlement" className="rounded-full bg-[#C86650] hover:bg-[#B4553F]"><DollarSign className="w-4 h-4 mr-2" />Apply settlement offer</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Settlement Offer</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="text-sm text-[#5C6A64]">
            Past-due balance: <b className="text-[#C86650]">${balance.toFixed(2)}</b> · Deadline: <b>{deadline || "—"}</b>
            <p className="text-xs mt-1">Edit the letter deadline from the Delinquency tab header.</p>
          </div>
          <Select value={option} onValueChange={setOption}>
            <SelectTrigger data-testid="settlement-option" className="rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="option1_65_paid_in_full">Option 1 — 65% reduction (pay in full within 30 days)</SelectItem>
              <SelectItem value="option2_50_payment_plan">Option 2 — 50% reduction (payment plan, deposit required)</SelectItem>
            </SelectContent>
          </Select>
          {preview && (
            <div className="p-3 rounded-xl bg-[#FDF9F1] border border-[#EADCC8] text-sm">
              <p>Discount: <b className="text-[#2C4C3B]">− ${preview.discount_amount.toFixed(2)}</b></p>
              <p>Amount due after discount: <b className="text-[#C86650]">${preview.amount_due.toFixed(2)}</b></p>
            </div>
          )}
          {option === "option2_50_payment_plan" && (
            <>
              <div className="border-t border-[#EADCC8] pt-3">
                <p className="text-xs uppercase tracking-wider text-[#5C6A64] mb-2">Deposit (optional)</p>
                <p className="text-xs text-[#5C6A64] mb-2">Leave blank if no deposit collected — the full settled amount will be scheduled into installments.</p>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Deposit ($)" testid="settlement-deposit" type="number" value={form.deposit_amount} onChange={(v) => setForm({ ...form, deposit_amount: v })} />
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Method</label>
                    <Select value={form.deposit_method} onValueChange={(v) => setForm({ ...form, deposit_method: v })}>
                      <SelectTrigger data-testid="settlement-dep-method" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="check">Check</SelectItem>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                        <SelectItem value="money_order">Money order</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Field label="Reference" testid="settlement-dep-ref" value={form.deposit_reference} onChange={(v) => setForm({ ...form, deposit_reference: v })} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <Field label="Installments" testid="settlement-installments" type="number" value={form.installments} onChange={(v) => setForm({ ...form, installments: v })} help="Ignored if a monthly amount is set." />
                <Field label="Monthly amount ($)" testid="settlement-monthly" type="number" value={form.monthly_amount} onChange={(v) => setForm({ ...form, monthly_amount: v })} help="Overrides count." />
                <Field label="First due" testid="settlement-first-due" type="date" value={form.first_due_date} onChange={(v) => setForm({ ...form, first_due_date: v })} />
                <div>
                  <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Frequency</label>
                  <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                    <SelectTrigger data-testid="settlement-frequency" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="bi-weekly">Bi-weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Notes to homeowner (optional)</label>
            <Textarea data-testid="settlement-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 rounded-xl" />
          </div>
          <Button data-testid="apply-settlement" onClick={apply} disabled={busy} className="w-full rounded-full bg-[#2C4C3B]">
            {busy ? "Applying…" : `Apply ${option === "option1_65_paid_in_full" ? "Option 1" : "Option 2"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LetterDialog({ propertyId, balance, deadline, property }) {
  const [open, setOpen] = useState(false);
  const [tpl, setTpl] = useState("");
  const [customBody, setCustomBody] = useState("");
  const [customDeadline, setCustomDeadline] = useState(deadline || "");
  const [customBalance, setCustomBalance] = useState(String(balance || 0));
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/settings/letter-template").then(({ data }) => {
      setTpl(data.template);
      setCustomBody(data.template);
    });
    setCustomDeadline(deadline || "");
    setCustomBalance(String(balance || 0));
    // `api` + setState refs are stable; other referenced items are the deps below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, deadline, balance]);

  const download = async () => {
    setBusy(true);
    try {
      const body = customBody !== tpl ? customBody : null;
      const balOverride = parseFloat(customBalance);
      const payload = {
        body,
        deadline_override: customDeadline || null,
        balance_override: !isNaN(balOverride) && balOverride !== balance ? balOverride : null,
        save_as_template: saveAsTemplate && !!body,
      };
      const res = await api.post(`/admin/properties/${propertyId}/settlement/letter.pdf`, payload, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `settlement_letter_${(property?.unit_number || "unit").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(saveAsTemplate ? "PDF downloaded · template saved" : "PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate PDF");
    } finally { setBusy(false); }
  };

  const emailLetter = async () => {
    if (!property?.owner_email) return toast.error("This property has no email on file — add one from the Edit property card first.");
    if (!window.confirm(`Send this letter with the PDF attached to ${property.owner_email}?`)) return;
    setBusy(true);
    try {
      const body = customBody !== tpl ? customBody : null;
      const balOverride = parseFloat(customBalance);
      const payload = {
        body,
        deadline_override: customDeadline || null,
        balance_override: !isNaN(balOverride) && balOverride !== balance ? balOverride : null,
        save_as_template: saveAsTemplate && !!body,
      };
      const { data } = await api.post(`/admin/properties/${propertyId}/settlement/letter/email`, payload);
      if (data.ok) toast.success(`Letter emailed to ${data.sent_to}`);
      else toast.error("Send failed — check the Board email logs");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="open-letter-dialog" variant="outline" className="rounded-full border-[#2C4C3B] text-[#2C4C3B]">
          Generate settlement letter PDF
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl">Settlement Letter — {property?.address} Unit {property?.unit_number}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-[#5C6A64]">
            Edit the letter below for custom terms (deposit waivers, extended deadlines, one-off arrangements, etc.). Placeholders like <code>{"{owner_name}"}</code>, <code>{"{balance}"}</code>, <code>{"{deadline}"}</code>, <code>{"{amount_due_option1}"}</code>, <code>{"{amount_due_option2}"}</code> will be substituted at download.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Deadline (overrides global)</label>
              <Input data-testid="letter-deadline" type="date" value={customDeadline} onChange={(e) => setCustomDeadline(e.target.value)} className="mt-1 rounded-xl" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Balance used in letter</label>
              <Input data-testid="letter-balance" type="number" step="0.01" value={customBalance} onChange={(e) => setCustomBalance(e.target.value)} className="mt-1 rounded-xl" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#5C6A64]">Letter body</label>
            <Textarea data-testid="letter-body" rows={18} value={customBody} onChange={(e) => setCustomBody(e.target.value)} className="mt-1 rounded-xl font-mono text-xs" />
          </div>
          <label className="flex items-start gap-2 text-sm text-[#1A2421] cursor-pointer p-3 bg-[#FDF9F1] border border-[#EADCC8] rounded-xl">
            <input type="checkbox" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} data-testid="letter-save-tpl" className="mt-1" />
            <span>Save these edits as the new default template for future letters.</span>
          </label>
          <div className="flex gap-3 flex-wrap">
            <Button data-testid="download-letter" onClick={download} disabled={busy} className="flex-1 rounded-full bg-[#2C4C3B]">
              {busy ? "Working…" : "Download PDF"}
            </Button>
            <Button data-testid="email-letter" onClick={emailLetter} disabled={busy} className="flex-1 rounded-full bg-[#C86650] hover:bg-[#B4553F]">
              {busy ? "Working…" : `Email to ${property?.owner_email ? property.owner_email.split("@")[0] : "homeowner"}`}
            </Button>
            <Button variant="outline" onClick={() => setCustomBody(tpl)} className="rounded-full border-[#D5CFC4]">Reset to template</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
